import { cn } from '@commenda/utils';
import { ColumnResizeControl } from '../types';

// Height + colors live in injected CSS rather than Tailwind classes: arbitrary
// values / data-variants authored inside kakashi aren't reliably generated by
// the consuming app's build (same reason TableRow injects its row-hover CSS), and
// a dropped `h-[calc(...)]` left the handle with no height and no grab area.
//   - height: real hit area + 1px to cover each cell's bottom border (no seam).
//   - base: subtle border color on hover.
//   - [data-dtresizing]: brand color while resizing, applied via the container
//     attribute so it also reaches body rows that are frozen mid-drag.
const RESIZE_HANDLE_STYLE_ID = 'kakashi-datatable-resize-handle';
const RESIZE_HANDLE_CSS = `
.dt-resize-handle {
  height: calc(100% + 1px);
  background-color: var(--color-border-secondary);
}
[data-dtresizing='true'] .dt-resize-handle {
  background-color: var(--color-fg-brand);
}
`;
let resizeHandleStyleInjected = false;
const ensureResizeHandleStyle = () => {
  if (resizeHandleStyleInjected || typeof document === 'undefined') return;
  if (document.getElementById(RESIZE_HANDLE_STYLE_ID)) {
    resizeHandleStyleInjected = true;
    return;
  }
  const el = document.createElement('style');
  el.id = RESIZE_HANDLE_STYLE_ID;
  el.textContent = RESIZE_HANDLE_CSS;
  document.head.appendChild(el);
  resizeHandleStyleInjected = true;
};

/**
 * Draggable column-resize handle, rendered in the header cell and every body cell
 * of a resizable column so the edge can be grabbed from any row. Cells of a column
 * share one hover state (via `control`), lighting the full top-to-bottom line like
 * the sidebar splitter. The parent cell must be `relative`.
 */
export function ColumnResizeHandle({ control }: { control: ColumnResizeControl }) {
  ensureResizeHandleStyle();
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      onMouseDown={control.onResizeStart}
      onTouchStart={control.onResizeStart}
      onMouseEnter={control.onHoverStart}
      onMouseLeave={control.onHoverEnd}
      // Stop click bubbling so grabbing the edge never triggers the row/header click.
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'dt-resize-handle absolute right-0 top-0 w-1 cursor-col-resize select-none touch-none z-10',
        control.isActive ? 'opacity-100' : 'opacity-0'
      )}
    />
  );
}

export default ColumnResizeHandle;
